import { Box } from "@primer/react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";

import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { DashBoardModel } from "../../models/response/thu-phi/DashBoardModel";
import { phanBoPhiApi } from "../../api/phanBoPhiApi";
import { useSelector } from "react-redux";
import { RootState } from "../../state/reducers";

// Đăng ký các thành phần cho biểu đồ
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ChartDoanhThuTheoKhoi = () => {
  const [dashBoardModel, setDashBoardModel] = useState<DashBoardModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { dm_coso_id, nam_hoc } = useSelector(
    (state: RootState) => state.common
  );

  useEffect(() => {
    const handleReloadDashBoard = async () => {
      try {
        setIsLoading(true);
        const res = await phanBoPhiApi.SelectDashBoard({
          dm_coso_id,
          nam_hoc,
        });
        if (res.is_success) {
          setDashBoardModel(res.data);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    handleReloadDashBoard();
  }, [dm_coso_id, nam_hoc]);

  // Tính tổng doanh thu theo từng khối
  const dataSource = useMemo(() => {
    // Group data theo dm_truong_id + ten_khoi và tính tổng so_tien
    const groupedData = dashBoardModel.reduce((acc, item) => {
      const existingKhoi = acc.find(x =>
        x.ten_khoi === item.ten_khoi && x.dm_truong_id === item.dm_truong_id
      );
      if (existingKhoi) {
        existingKhoi.so_tien += item.so_tien;
      } else {
        acc.push({
          ten_khoi: item.ten_khoi,
          dm_khoi_id: item.dm_khoi_id,
          dm_truong_id: item.dm_truong_id,
          so_tien: item.so_tien,
        });
      }
      return acc;
    }, [] as Array<{ ten_khoi: string; dm_khoi_id: number; dm_truong_id: number; so_tien: number }>);

    // Sắp xếp theo dm_truong_id trước, sau đó theo dm_khoi_id
    return groupedData.sort((a, b) => {
      // Sắp xếp theo trường trước
      if (a.dm_truong_id !== b.dm_truong_id) {
        return a.dm_truong_id - b.dm_truong_id;
      }
      // Nếu cùng trường, sắp xếp theo khối
      return a.dm_khoi_id - b.dm_khoi_id;
    });
  }, [dashBoardModel]);

  // Format số tiền thành VND
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Chuẩn bị dữ liệu cho Bar Chart
  const data = {
    labels: dataSource.map((item) => item.ten_khoi), // Nhãn các khối
    datasets: [
      {
        label: "Doanh thu (VND)",
        data: dataSource.map((item) => item.so_tien), // Số tiền tương ứng
        backgroundColor: [
          "#006bff"
        ],
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
        borderRadius: 8, // Bo tròn góc
        borderSkipped: false,
      },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        display: true,
        labels: {
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#182D62',
        },
      },
      title: {
        display: true,
        text: "Tổng doanh thu theo từng khối",
        font: {
          size: 18,
          weight: 'bold',
        },
        color: '#182D62',
        padding: 20,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#182D62',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 12,
          },
          color: '#666',
          callback: function(value: any) {
            return formatCurrency(value);
          }
        },
        title: {
          display: true,
          text: 'Doanh thu (VND)',
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#182D62',
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: 'bold',
          },
          color: '#182D62',
        },
        title: {
          display: true,
          text: 'Khối',
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#182D62',
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  };
  // Component Loading
  const LoadingComponent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "400px",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          border: "4px solid #f3f3f3",
          borderTop: "4px solid #006bff",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          "@keyframes spin": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      />
      <Box
        sx={{
          fontSize: "1rem",
          color: "#666",
          fontWeight: 500,
        }}
      >
        Đang tải dữ liệu...
      </Box>
    </Box>
  );

  // Nếu đang loading, hiển thị loading component
  if (isLoading) {
    return (
      <Box sx={{
        padding: 3,
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        height: '100%'
      }}>
        <LoadingComponent />
      </Box>
    );
  }

  return (
    <Box sx={{
      padding: 3,
      backgroundColor: '#fff',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      height: '100%'
    }}>
      <Box sx={{
        marginBottom: 3,
        textAlign: 'center',
        borderBottom: '1px solid #ddd',
        paddingBottom: 2
      }}>
        <Box sx={{
          fontSize: 18,
          fontWeight: 600,
          color: '#666',
          marginBottom: 1
        }}>
          Doanh thu theo Khối
        </Box>
        <Box sx={{
          fontSize: "1.1rem",
          color: "fg.muted",
          fontStyle: 'italic'
        }}>
          Tổng hợp doanh thu từ các khoản thu theo từng khối học
        </Box>
        {dataSource.length > 0 && (
          <Box sx={{
            fontSize: "0.9rem",
            color: "#666",
            marginTop: 1
          }}>
            Tổng cộng: {dataSource.length} khối |
            Tổng doanh thu: {formatCurrency(dataSource.reduce((sum, item) => sum + item.so_tien, 0))}
          </Box>
        )}
      </Box>

      <Box sx={{
        height: 400,
        width: "100%",
        position: 'relative'
      }}>
        {dataSource.length > 0 ? (
          <Bar data={data} options={options} />
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            fontSize: '1.2rem',
            color: '#666',
            fontStyle: 'italic'
          }}>
            📈 Không có dữ liệu để hiển thị
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChartDoanhThuTheoKhoi;
